import { useState } from "react";
import { Reveal } from "@/hooks/useReveal";
import { toast } from "sonner";
import { MapPin, Phone, Mail } from "lucide-react";
import { api } from "@/lib/api";

const Field = ({ label, name, type = "text", value, onChange, required, testid, multiline }) => (
  <label className="block">
    <span className="block text-[10px] tracking-[0.28em] uppercase text-black/50 mb-3">
      {label}
      {required && <span className="text-black/30"> *</span>}
    </span>
    {multiline ? (
      <textarea
        data-testid={testid}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        rows={4}
        className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base text-black focus:outline-none focus:border-black transition-colors resize-none"
      />
    ) : (
      <input
        data-testid={testid}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base text-black focus:outline-none focus:border-black transition-colors"
      />
    )}
  </label>
);

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      if (data.email_sent) {
        toast.success("Message sent. Expect a reply from the Manzano team shortly.");
      } else {
        toast.success("Message received. We'll be in touch shortly.");
      }
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send right now — please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-white py-28 md:py-40 px-6 md:px-12"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="overline mb-6">Contact Us</p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-black">
              Begin a
              <em className="italic font-light"> conversation</em>.
            </h2>
          </Reveal>
          <Reveal delay={220}>
            <p className="text-base md:text-lg text-black/60 leading-relaxed mt-10 max-w-md">
              Whether you are buying, selling, or simply considering the market — every
              inquiry is met by a senior advisor, never an inbox.
            </p>
          </Reveal>

          <Reveal delay={320}>
            <div className="mt-14 space-y-8">
              <div className="flex items-start gap-5" data-testid="contact-address">
                <MapPin size={18} className="text-black mt-1 shrink-0" />
                <div>
                  <p className="overline mb-1">Office</p>
                  <p className="text-sm text-black/80 leading-relaxed">
                    27600 Bouquet Canyon Rd.
                    <br />
                    Santa Clarita, California 91350
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-5" data-testid="contact-phone">
                <Phone size={18} className="text-black mt-1 shrink-0" />
                <div>
                  <p className="overline mb-1">Direct</p>
                  <p className="text-sm text-black/80">(661) 212-1300</p>
                </div>
              </div>
              <div className="flex items-start gap-5" data-testid="contact-email">
                <Mail size={18} className="text-black mt-1 shrink-0" />
                <div>
                  <p className="overline mb-1">Email</p>
                  <p className="text-sm text-black/80">amanzano4reo@gmail.com</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={200}>
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10"
            >
              <Field
                label="Full Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                testid="contact-input-name"
              />
              <Field
                label="Email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                testid="contact-input-email"
              />
              <Field
                label="Phone"
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                testid="contact-input-phone"
              />
              <Field
                label="Interest"
                name="interest"
                value={form.interest}
                onChange={handleChange}
                testid="contact-input-interest"
              />
              <div className="md:col-span-2">
                <Field
                  label="Message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  multiline
                  required
                  testid="contact-input-message"
                />
              </div>
              <div className="md:col-span-2 mt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center justify-center px-10 py-4 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] font-medium hover:bg-white hover:text-black transition-all duration-500 disabled:opacity-50"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
